export const ASSESSMENT_QUESTION_FOCUS_PARAM = "focusSystemData";
export const ASSESSMENT_QUESTION_ID_PARAM = "focusQuestionId";
export const ASSESSMENT_GAP_ID_PARAM = "focusGapId";
export const REPORT_CASE_CODE_PARAM = "reportCaseCode";
export const REPORT_CASE_ID_PARAM = "reportCaseId";

export const readAssessmentQuestionFocusParams = (search = "") => {
    const params = new URLSearchParams(search);

    return {
        focusSystemData: params.get(ASSESSMENT_QUESTION_FOCUS_PARAM),
        focusQuestionId: params.get(ASSESSMENT_QUESTION_ID_PARAM),
        focusGapId: params.get(ASSESSMENT_GAP_ID_PARAM),
        reportCaseCode: params.get(REPORT_CASE_CODE_PARAM),
        reportCaseId: params.get(REPORT_CASE_ID_PARAM)
    };
};

export const hasAssessmentQuestionFocus = (focus) => {
    if (!focus || typeof focus !== "object") {
        return false;
    }

    return Boolean(
        focus.focusGapId
        || focus.focusQuestionId
        || focus.focusSystemData
        || focus.gapId
        || focus.questionId
        || focus.systemData
    );
};

/**
 * Case identifier for questionnaire fetches.
 * Deep links send reportCaseCode when present, otherwise reportCaseId.
 *
 * @param {{ reportCaseCode?: string|null, reportCaseId?: string|number|null }} params
 * @returns {string|null}
 */
export const resolveLinkedReportCaseId = ({ reportCaseCode, reportCaseId } = {}) => {
    if (reportCaseCode) {
        return String(reportCaseCode);
    }

    if (reportCaseId != null && reportCaseId !== "") {
        return String(reportCaseId);
    }

    return null;
};

const collectReportCaseIdentifiers = (report) => {
    return [
        report?.case_id,
        report?.caseId,
        report?.id,
        report?.report_case_id,
        report?.reportCaseId,
        report?.report_case_code,
        report?.reportCaseCode,
    ]
        .filter((value) => value != null && value !== "")
        .map((value) => String(value));
};

const reportMatchesFocus = (report, { reportCaseCode, reportCaseId } = {}) => {
    const identifiers = collectReportCaseIdentifiers(report);

    if (
        reportCaseCode
        && identifiers.includes(String(reportCaseCode))
    ) {
        return true;
    }

    if (
        reportCaseId != null
        && reportCaseId !== ""
        && identifiers.includes(String(reportCaseId))
    ) {
        return true;
    }

    return false;
};

export const findReportAccordionIndex = (reports, focus = {}) => {
    if (!Array.isArray(reports) || reports.length === 0) {
        return null;
    }

    const index = reports.findIndex((report) => reportMatchesFocus(report, focus));

    return index >= 0 ? index : null;
};

/**
 * Sidebar/API selection id for a report list row (e.g. Processing Activity).
 *
 * @param {Array<object>} reports
 * @param {{ reportCaseCode?: string|null, reportCaseId?: string|number|null }} focus
 * @returns {string|number|null}
 */
export const findReportSelectionCaseId = (reports, focus = {}) => {
    const index = findReportAccordionIndex(reports, focus);

    if (index == null) {
        return null;
    }

    const report = reports[index];

    return report?.case_id ?? report?.caseId ?? null;
};

export const reportCaseIdsEqual = (left, right) => {
    if (left == null || right == null) {
        return false;
    }

    return String(left) === String(right);
};

/**
 * Removes deep-link focus params so manual navigation is not overridden.
 *
 * @param {string} search
 * @returns {string}
 */
export const stripAssessmentQuestionFocusFromSearch = (search = "") => {
    const params = new URLSearchParams(search);

    params.delete(ASSESSMENT_QUESTION_FOCUS_PARAM);
    params.delete(ASSESSMENT_QUESTION_ID_PARAM);
    params.delete(ASSESSMENT_GAP_ID_PARAM);
    params.delete(REPORT_CASE_CODE_PARAM);
    params.delete(REPORT_CASE_ID_PARAM);

    return params.toString();
};

const escapeCssValue = (value) => {
    const text = String(value);

    if (typeof CSS !== "undefined" && CSS.escape) {
        return CSS.escape(text);
    }

    return text.replace(/"/g, '\\"');
};

const visitQuestionNodes = (nodes, visit) => {
    if (!Array.isArray(nodes)) {
        return;
    }

    nodes.forEach((node) => {
        if (!node || typeof node !== "object") {
            return;
        }

        visit(node);
        visitQuestionNodes(node.questions, visit);
        visitQuestionNodes(node.panels, visit);
        visitQuestionNodes(node.items, visit);
    });
};

const questionIdentity = (node) => {
    if (!node) {
        return null;
    }

    return node.getAttribute?.("data-assessment-question-id")
        || node.closest?.("[data-assessment-question-id]")
            ?.getAttribute("data-assessment-question-id")
        || null;
};

/**
 * Several markers can sit on the same question. Collapse them to one question
 * and refuse to guess when more than one question matches.
 *
 * @param {NodeList|Element[]} nodes
 * @returns {Element|null}
 */
export const pickUniqueQuestionElement = (nodes) => {
    const list = Array.from(nodes || []).filter(Boolean);

    if (list.length === 0) {
        return null;
    }

    const unique = new Map();

    list.forEach((node) => {
        const root = node.closest?.(".question-item")
            || node.closest?.("[data-assessment-question-id]")
            || node;
        const key = questionIdentity(root) || questionIdentity(node) || root;

        if (!unique.has(key)) {
            unique.set(key, node);
        }
    });

    if (unique.size !== 1) {
        return null;
    }

    return unique.values().next().value;
};

const queryMarkedElements = (attribute, value) => {
    if (value == null || value === "") {
        return [];
    }

    return document.querySelectorAll(
        `[${attribute}="${escapeCssValue(value)}"]`
    );
};

/**
 * Questionnaire field id for a question key. Returns a value only when exactly
 * one question in the tree uses that system_data.
 *
 * @param {Array<object>|object|null|undefined} questions
 * @param {string} systemData
 * @returns {string|number|null}
 */
export const findAssessmentQuestionId = (questions, systemData) => {
    if (!systemData) {
        return null;
    }

    const target = String(systemData);
    const matches = [];

    visitQuestionNodes(Array.isArray(questions) ? questions : [questions], (node) => {
        if (!node.type) {
            return;
        }

        const key = node.systemData || node.system_data;

        if (key != null && String(key) === target && node.id != null) {
            matches.push(node.id);
        }
    });

    const uniqueIds = Array.from(new Set(matches.map((id) => String(id))));

    return uniqueIds.length === 1 ? matches[0] : null;
};

const findElementByQuestionId = (questionId) => {
    if (questionId == null || questionId === "") {
        return null;
    }

    const marked = pickUniqueQuestionElement(
        queryMarkedElements("data-assessment-question-id", questionId)
    );

    if (marked) {
        return marked;
    }

    const id = String(questionId);

    return document.getElementById(id)
        || document.querySelector(`label[for="${escapeCssValue(id)}"]`)
        || document.querySelector(`[name="${escapeCssValue(id)}"]`);
};

const normalizeFocus = (focus) => {
    if (typeof focus === "string") {
        return {
            systemData: focus,
            questionId: null,
            gapId: null
        };
    }

    return {
        systemData: focus?.systemData || focus?.focusSystemData || null,
        questionId: focus?.questionId || focus?.focusQuestionId || null,
        gapId: focus?.gapId || focus?.focusGapId || null
    };
};

const findQuestionNodeByFocus = (questions, focus) => {
    const { systemData, questionId, gapId } = normalizeFocus(focus);
    const nodes = Array.isArray(questions) ? questions : [questions];
    const gapMatches = [];
    const questionMatches = [];
    const systemDataMatches = [];

    visitQuestionNodes(nodes, (node) => {
        if (!node?.type) {
            return;
        }

        if (
            gapId
            && node.assessment_gap
            && String(node.assessment_gap.id) === String(gapId)
        ) {
            gapMatches.push(node);
            return;
        }

        if (questionId != null && String(node.id) === String(questionId)) {
            questionMatches.push(node);
            return;
        }

        if (systemData) {
            const key = node.systemData || node.system_data;

            if (key != null && String(key) === String(systemData)) {
                systemDataMatches.push(node);
            }
        }
    });

    if (gapMatches.length === 1) {
        return gapMatches[0];
    }

    if (questionMatches.length === 1) {
        return questionMatches[0];
    }

    if (systemDataMatches.length === 1) {
        return systemDataMatches[0];
    }

    return null;
};

const findQuestionElement = (focus, questions) => {
    const { systemData, questionId, gapId } = normalizeFocus(focus);

    if (gapId) {
        const byGap = pickUniqueQuestionElement(
            queryMarkedElements("data-assessment-gap-id", gapId)
        );

        if (byGap) {
            return byGap;
        }
    }

    if (questionId) {
        const byQuestion = findElementByQuestionId(questionId);

        if (byQuestion) {
            return byQuestion;
        }
    }

    if (systemData) {
        const bySystemData = pickUniqueQuestionElement(
            queryMarkedElements("data-assessment-question-system-data", systemData)
        );

        if (bySystemData) {
            return bySystemData;
        }
    }

    const matchedQuestion = findQuestionNodeByFocus(questions, focus);

    if (matchedQuestion?.id != null) {
        return findElementByQuestionId(matchedQuestion.id);
    }

    if (systemData) {
        return findElementByQuestionId(
            findAssessmentQuestionId(questions, systemData)
        );
    }

    return null;
};

const activateQuestionPage = (element) => {
    const page = element.closest("[data-assessment-page-id]");

    if (!page || page.classList.contains("active")) {
        return false;
    }

    const pageId = page.getAttribute("data-assessment-page-id");
    const tab = document.querySelector(
        `[data-tab-id="${escapeCssValue(pageId)}"]`
    );

    if (!tab) {
        return false;
    }

    tab.click();

    return true;
};

const openCollapsedAccordions = (element) => {
    const items = [];
    let node = element.parentElement;

    while (node) {
        if (node.classList && node.classList.contains("accordion-item")) {
            items.push(node);
        }

        node = node.parentElement;
    }

    let opened = false;

    items.reverse().forEach((item) => {
        const button = item.querySelector(
            ":scope > .accordion-header .accordion-button, :scope > .accordion-button"
        );

        if (button && button.classList.contains("collapsed")) {
            button.click();
            opened = true;
        }
    });

    return opened;
};

const highlightQuestionElement = (element, highlightMs) => {
    const target = element.closest(".question-item")
        || element.closest(".form-group")
        || element;

    target.classList.add("assessment-question-focus-highlight");
    target.style.scrollMarginTop = "120px";
    target.scrollIntoView({
        behavior: "smooth",
        block: "center"
    });

    window.setTimeout(() => {
        target.classList.remove("assessment-question-focus-highlight");
    }, highlightMs);
};

export const focusAssessmentQuestionElement = (
    focus,
    { delayMs = 400, highlightMs = 4500, questions = null } = {}
) => {
    if (typeof document === "undefined" || !hasAssessmentQuestionFocus(focus)) {
        return false;
    }

    const attempt = (triesLeft) => {
        const element = findQuestionElement(focus, questions);

        if (!element) {
            if (triesLeft > 0) {
                window.setTimeout(() => attempt(triesLeft - 1), 400);
            }

            return;
        }

        const openedPage = activateQuestionPage(element);
        const openedAccordion = openCollapsedAccordions(element);

        window.setTimeout(() => {
            highlightQuestionElement(
                findQuestionElement(focus, questions) || element,
                highlightMs
            );
        }, openedPage || openedAccordion ? 350 : 0);
    };

    window.setTimeout(() => attempt(12), Math.max(0, delayMs));

    return true;
};
